import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ToastContainer } from './ToastContainer';

const ToastContext = createContext(null);

let idCounter = 0;
/** Generates a unique ID for each toast instance */
const generateId = () => `toast-${++idCounter}`;

/**
 * ToastProvider — Manages a queue-based notification system.
 *
 * How the queue works:
 *   - New toasts are added to `queue` (waiting list).
 *   - Only 1 toast displays at a time (`activeToasts`).
 *   - When the active toast closes, the next one from the queue is promoted.
 *   - This prevents toast stacking / overlap.
 *
 * Wrap your app once (alongside ThemeProvider):
 *   <ThemeProvider>
 *     <ToastProvider>
 *       <App />
 *     </ToastProvider>
 *   </ThemeProvider>
 */
export const ToastProvider = ({ children }) => {
    const [queue, setQueue] = useState([]);         // Pending toasts waiting to display
    const [activeToasts, setActiveToasts] = useState([]); // Currently visible toast(s)

    // Process queue: promote the next toast when there's nothing on screen
    useEffect(() => {
        if (queue.length > 0 && activeToasts.length === 0) {
            const nextToast = queue[0];
            setActiveToasts([nextToast]);
            setQueue(prev => prev.slice(1)); // Remove promoted toast from queue
        }
    }, [queue, activeToasts]);

    /** Add a toast to the queue. Returns the toast's unique ID. */
    const addToast = useCallback((toastOptions) => {
        const id = generateId();
        const newToast = { ...toastOptions, id };
        
        setQueue(prev => [...prev, newToast]);
        return id;
    }, []);

    /** Remove a toast by ID (called when toast auto-dismisses or user clicks close) */
    const removeToast = useCallback((id) => {
        setActiveToasts(prev => prev.filter(toast => toast.id !== id));
    }, []);

    // ─── Semantic shorthand methods ─────────────────────────────────
    // Usage: toast.success('Saved!') instead of toast.addToast({ type: 'success', message: '...' })
    // Each type has its own default duration (overridable via options.duration)

    const success = useCallback((message, options = {}) => {
        return addToast({ duration: 3000, type: 'success', message, ...options });
    }, [addToast]);

    const error = useCallback((message, options = {}) => {
        return addToast({ duration: 7000, type: 'error', message, ...options });
    }, [addToast]);

    const warning = useCallback((message, options = {}) => {
        return addToast({ duration: 5000, type: 'warning', message, ...options });
    }, [addToast]);

    const info = useCallback((message, options = {}) => {
        return addToast({ duration: 5000, type: 'info', message, ...options });
    }, [addToast]);

    const contextValue = {
        addToast,
        removeToast,
        success,
        error,
        warning,
        info
    };

    return (
        <ToastContext.Provider value={contextValue}>
            {children}
            {/* Portal to body so toasts always render above popups (z-index stacking) */}
            {createPortal(
                <ToastContainer toasts={activeToasts} removeToast={removeToast} />,
                document.body
            )}
        </ToastContext.Provider>
    );
};

/**
 * useToast — Access toast methods from any component.
 *
 * Returns: { addToast, removeToast, success, error, warning, info }
 *
 * Must be called inside a <ToastProvider>.
 */
export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};